// -------------------------------------------------------------------------
// LCS-based character diff for the admin Result Detail report.
// PRESENTATION-ONLY. Never used to (re)compute accuracy — the backend's
// existing stored `accuracy` / `wpm` / `field_accuracies` remain the source
// of truth. This helper only categorizes and explains mistakes clearly.
// -------------------------------------------------------------------------

// Classic Longest-Common-Subsequence diff → produces a minimal edit script
// (equal / delete / insert). Handles insertions, deletions and replacements
// without cascading errors after a single mismatch.
export function lcsDiff(original, typed) {
  const a = Array.from(original || "");
  const b = Array.from(typed || "");
  const n = a.length, m = b.length;
  // dp[i][j] = LCS length of a[i:] and b[j:]
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = []; // { type: 'eq'|'del'|'ins', oIndex, tIndex, char }
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "eq", oIndex: i, tIndex: j, char: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "del", oIndex: i, tIndex: j, char: a[i] }); // in original, not in typed
      i++;
    } else {
      ops.push({ type: "ins", oIndex: i, tIndex: j, char: b[j] }); // extra in typed
      j++;
    }
  }
  while (i < n) { ops.push({ type: "del", oIndex: i, tIndex: j, char: a[i] }); i++; }
  while (j < m) { ops.push({ type: "ins", oIndex: i, tIndex: j, char: b[j] }); j++; }
  return ops;
}

// Collapse a raw LCS edit script into categorized, human-readable mistakes.
// Adjacent del+ins pairs become "Wrong / Replaced". Runs of dels/inserts
// become "Missing Segment" / "Extra Segment" for readability.
export function categorizeMistakes(ops) {
  const mistakes = [];
  const n = ops.length;
  let idx = 0;
  while (idx < n) {
    const op = ops[idx];
    if (op.type === "eq") { idx++; continue; }

    // Collect a run of consecutive non-eq ops
    let runEnd = idx;
    while (runEnd < n && ops[runEnd].type !== "eq") runEnd++;
    const run = ops.slice(idx, runEnd);
    const dels = run.filter((o) => o.type === "del");
    const inss = run.filter((o) => o.type === "ins");

    // Pair up del+ins as replacements
    const pairCount = Math.min(dels.length, inss.length);
    for (let k = 0; k < pairCount; k++) {
      const d = dels[k];
      const iOp = inss[k];
      mistakes.push({
        category: classifyReplacement(d.char, iOp.char),
        position: d.oIndex + 1, // 1-based for humans
        expected: d.char,
        typed: iOp.char,
      });
    }
    const remainingDels = dels.slice(pairCount);
    const remainingInss = inss.slice(pairCount);

    // Group leftover missing chars into segments (each contiguous by position)
    groupContiguous(remainingDels, "oIndex").forEach((segment) => {
      const text = segment.map((o) => o.char).join("");
      if (segment.length === 1) {
        const c = segment[0].char;
        mistakes.push({
          category: c === " " ? "MISSING_SPACE" : "MISSING",
          position: segment[0].oIndex + 1,
          expected: c,
          typed: null,
        });
      } else {
        mistakes.push({
          category: "MISSING_SEGMENT",
          position: segment[0].oIndex + 1,
          expected: text,
          typed: null,
        });
      }
    });

    groupContiguous(remainingInss, "tIndex").forEach((segment) => {
      const text = segment.map((o) => o.char).join("");
      if (segment.length === 1) {
        const c = segment[0].char;
        mistakes.push({
          category: c === " " ? "EXTRA_SPACE" : "EXTRA",
          position: segment[0].tIndex + 1,
          expected: null,
          typed: c,
        });
      } else {
        mistakes.push({
          category: "EXTRA_SEGMENT",
          position: segment[0].tIndex + 1,
          expected: null,
          typed: text,
        });
      }
    });

    idx = runEnd;
  }
  // Deterministic order: by position within original/typed
  mistakes.sort((x, y) => (x.position || 0) - (y.position || 0));
  return mistakes;
}

function classifyReplacement(expected, typed) {
  if (expected === typed) return "WRONG"; // shouldn't happen but safety
  if (expected.toLowerCase() === typed.toLowerCase()) return "CASE";
  const isSymbol = (c) => !/[A-Za-z0-9\s]/.test(c);
  if (isSymbol(expected) && isSymbol(typed)) return "SYMBOL";
  return "WRONG";
}

function groupContiguous(list, indexKey) {
  if (!list.length) return [];
  const out = [];
  let cur = [list[0]];
  for (let k = 1; k < list.length; k++) {
    if (list[k][indexKey] === list[k - 1][indexKey] + 1) cur.push(list[k]);
    else { out.push(cur); cur = [list[k]]; }
  }
  out.push(cur);
  return out;
}

// Summary counters — reporting only, never used to modify stored accuracy.
export function summarize(mistakes) {
  const s = {
    total: mistakes.length,
    missing: 0,
    extra: 0,
    wrong: 0,
    missing_space: 0,
    extra_space: 0,
    case: 0,
    symbol: 0,
  };
  for (const m of mistakes) {
    switch (m.category) {
      case "MISSING":
      case "MISSING_SEGMENT":
        s.missing += m.expected ? [...m.expected].length : 1;
        break;
      case "EXTRA":
      case "EXTRA_SEGMENT":
        s.extra += m.typed ? [...m.typed].length : 1;
        break;
      case "WRONG": s.wrong += 1; break;
      case "MISSING_SPACE": s.missing_space += 1; s.missing += 1; break;
      case "EXTRA_SPACE": s.extra_space += 1; s.extra += 1; break;
      case "CASE": s.case += 1; s.wrong += 1; break;
      case "SYMBOL": s.symbol += 1; s.wrong += 1; break;
      default: break;
    }
  }
  return s;
}

export const CATEGORY_LABEL = {
  MISSING: "Missing",
  MISSING_SEGMENT: "Missing Segment",
  EXTRA: "Extra",
  EXTRA_SEGMENT: "Extra Segment",
  WRONG: "Wrong / Replaced",
  MISSING_SPACE: "Missing Space",
  EXTRA_SPACE: "Extra Space",
  CASE: "Case Mismatch",
  SYMBOL: "Symbol Mismatch",
};

export function explain(m) {
  const showChar = (c) => (c === " " ? "[SPACE]" : c === null || c === undefined ? "—" : c);
  switch (m.category) {
    case "MISSING":
      return `Candidate missed "${showChar(m.expected)}" at position ${m.position}.`;
    case "MISSING_SEGMENT":
      return `Missing segment: "${m.expected}" starting at position ${m.position}.`;
    case "EXTRA":
      return `Candidate added extra "${showChar(m.typed)}" at position ${m.position}.`;
    case "EXTRA_SEGMENT":
      return `Extra segment typed: "${m.typed}" at position ${m.position}.`;
    case "WRONG":
      return `Expected "${showChar(m.expected)}", candidate typed "${showChar(m.typed)}" (position ${m.position}).`;
    case "MISSING_SPACE":
      return `Missing space at position ${m.position}.`;
    case "EXTRA_SPACE":
      return `Extra space at position ${m.position}.`;
    case "CASE":
      return `Case mismatch: expected "${m.expected}", typed "${m.typed}" (position ${m.position}).`;
    case "SYMBOL":
      return `Symbol mismatch: expected "${m.expected}", typed "${m.typed}" (position ${m.position}).`;
    default:
      return "Mistake detected.";
  }
}
